"use client";

import { c as _c } from "react/compiler-runtime";
import { useContext, useMemo, useCallback } from "react";
import { AuthContext } from "@/components/auth/Auth";
import { useWavesOverview } from "./useWavesOverview";
import { WAVE_FOLLOWING_WAVES_PARAMS } from "@/components/react-query-wrapper/utils/query-utils";
import { usePinnedWavesServer } from "./usePinnedWavesServer";
import type { ApiWave } from "@/generated/models/ApiWave";
import { useShowFollowingWaves } from "./useShowFollowingWaves";
import { ApiWaveType } from "@/generated/models/ApiWaveType";

// Enhanced wave interface with isPinned field and newDropsCount
interface EnhancedWave extends ApiWave {
  isPinned: boolean;
}

/**
 * Hook for managing and fetching waves list including pinned waves
 * @returns Wave list data and loading states
 */
const useWavesList = () => {
  const $ = _c(40);
  const {
    connectedProfile,
    activeProfileProxy
  } = useContext(AuthContext);
  const {
    pinnedIds,
    pinnedWaves: serverPinnedWaves,
    pinWave,
    unpinWave,
    isLoading: isPinnedWavesLoadingServer,
    isError: hasPinnedWavesErrorServer,
    refetch: refetchPinnedWaves
  } = usePinnedWavesServer();
  const [following] = useShowFollowingWaves();
  connectedProfile?.handle;
  const isConnectedIdentity = !!connectedProfile?.handle && !activeProfileProxy;
  const t0 = isConnectedIdentity && following;
  let t1;
  if ($[0] !== t0) {
    t1 = {
      type: WAVE_FOLLOWING_WAVES_PARAMS.initialWavesOverviewType,
      limit: WAVE_FOLLOWING_WAVES_PARAMS.limit,
      following: t0
    };
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    waves: mainWaves,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    status: mainWavesStatus,
    refetch: mainWavesRefetch
  } = useWavesOverview(t1);
  let map;
  if ($[2] !== mainWaves) {
    map = new Map();
    mainWaves.forEach(wave => map.set(wave.id, wave));
    $[2] = mainWaves;
    $[3] = map;
  } else {
    map = $[3];
  }
  const mainWavesMap = map;
  let t2;
  if ($[4] !== mainWavesMap) {
    t2 = new Set(mainWavesMap.keys());
    $[4] = mainWavesMap;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const mainWaveIds = t2;
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const missingPinnedIds = t3;
  let t4;
  if ($[7] !== mainWavesRefetch || $[8] !== refetchPinnedWaves) {
    t4 = () => {
      mainWavesRefetch();
      refetchPinnedWaves();
    };
    $[7] = mainWavesRefetch;
    $[8] = refetchPinnedWaves;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const refetchAllWaves = t4;
  let t5;
  if ($[10] !== mainWaveIds || $[11] !== serverPinnedWaves) {
    let t6;
    if ($[13] !== mainWaveIds) {
      t6 = wave_0 => !mainWaveIds.has(wave_0.id);
      $[13] = mainWaveIds;
      $[14] = t6;
    } else {
      t6 = $[14];
    }
    t5 = serverPinnedWaves.filter(t6);
    $[10] = mainWaveIds;
    $[11] = serverPinnedWaves;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const separatelyFetchedPinnedWaves = t5;
  let result;
  if ($[15] !== serverPinnedWaves) {
    result = [];
    serverPinnedWaves.forEach(wave_1 => {
      if (!waveIsDm(wave_1)) {
        result.push({
          ...wave_1,
          isPinned: true
        });
      }
    });
    $[15] = serverPinnedWaves;
    $[16] = result;
  } else {
    result = $[16];
  }
  const allPinnedWaves = result;
  let allWavesArray;
  if ($[17] !== mainWaves || $[18] !== pinnedIds || $[19] !== separatelyFetchedPinnedWaves) {
    const allWavesMap = new Map();
    mainWaves.forEach(wave_2 => {
      allWavesMap.set(wave_2.id, wave_2);
    });
    separatelyFetchedPinnedWaves.forEach(wave_3 => {
      allWavesMap.set(wave_3.id, wave_3);
    });
    let t6;
    if ($[21] !== pinnedIds) {
      t6 = new Set(pinnedIds);
      $[21] = pinnedIds;
      $[22] = t6;
    } else {
      t6 = $[22];
    }
    const pinnedWavesSet = t6;
    allWavesArray = [];
    [...mainWaves, ...separatelyFetchedPinnedWaves].forEach(wave_4 => {
      if (!allWavesMap.has(wave_4.id)) {
        return;
      }
      allWavesMap.delete(wave_4.id);
      const isPinned = pinnedWavesSet.has(wave_4.id);
      if (!waveIsDm(wave_4)) {
        allWavesArray.push({
          ...wave_4,
          isPinned
        });
      }
    });
    allWavesArray.sort(_temp);
    $[17] = mainWaves;
    $[18] = pinnedIds;
    $[19] = separatelyFetchedPinnedWaves;
    $[20] = allWavesArray;
  } else {
    allWavesArray = $[20];
  }
  const combinedWaves = allWavesArray;
  const allWaves = combinedWaves;
  const isPinnedWavesLoading = isPinnedWavesLoadingServer;
  const hasPinnedWavesError = hasPinnedWavesErrorServer;
  let t6;
  if ($[23] !== fetchNextPage) {
    t6 = () => fetchNextPage();
    $[23] = fetchNextPage;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  const fetchNextPageStable = t6;
  let t7;
  if ($[25] !== allPinnedWaves || $[26] !== allWaves || $[27] !== fetchNextPageStable || $[28] !== hasNextPage || $[29] !== hasPinnedWavesError || $[30] !== isFetching || $[31] !== isFetchingNextPage || $[32] !== isPinnedWavesLoading || $[33] !== mainWaves || $[34] !== mainWavesRefetch || $[35] !== mainWavesStatus || $[36] !== pinWave || $[37] !== refetchAllWaves || $[38] !== unpinWave) {
    t7 = {
      waves: allWaves,
      isFetching,
      isFetchingNextPage,
      hasNextPage,
      fetchNextPage: fetchNextPageStable,
      status: mainWavesStatus,
      pinnedWaves: allPinnedWaves,
      isPinnedWavesLoading,
      hasPinnedWavesError,
      addPinnedWave: pinWave,
      removePinnedWave: unpinWave,
      mainWaves,
      missingPinnedIds,
      mainWavesRefetch,
      refetchAllWaves
    };
    $[25] = allPinnedWaves;
    $[26] = allWaves;
    $[27] = fetchNextPageStable;
    $[28] = hasNextPage;
    $[29] = hasPinnedWavesError;
    $[30] = isFetching;
    $[31] = isFetchingNextPage;
    $[32] = isPinnedWavesLoading;
    $[33] = mainWaves;
    $[34] = mainWavesRefetch;
    $[35] = mainWavesStatus;
    $[36] = pinWave;
    $[37] = refetchAllWaves;
    $[38] = unpinWave;
    $[39] = t7;
  } else {
    t7 = $[39];
  }
  return t7;
};
const waveIsDm = (w: ApiWave) => w.wave.type === ApiWaveType.Chat && (w.chat as any)?.scope?.group?.is_direct_message === true;
export default useWavesList;
function _temp(a, b) {
  return b.metrics.latest_drop_timestamp - a.metrics.latest_drop_timestamp;
}